import { HttpErrorResponse } from '@angular/common/http';
import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  provideAppInitializer,
  Provider,
} from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { UnleashClient } from 'unleash-proxy-client';
import { beforeSend } from './before-send';

export interface SentryConfig {
  enabled?: boolean;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  unleash?: boolean;
}

export const initializeSentry = (dsn: string, config?: SentryConfig) => {
  Sentry.init({
    beforeSend,
    dsn,
    enabled: config?.enabled === true,
    environment: config?.environment,
    release: config?.release,
    integrations: [
      // Report only these HTTP statuses; other failures remain available to application error handling.
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [400, 403, 405, 406, 415, 422],
      }),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      ...(config?.unleash === true
        ? [Sentry.unleashIntegration({ featureFlagClientClass: UnleashClient })]
        : []),
    ],
    tracesSampleRate: config?.tracesSampleRate ?? 0.05,
    replaysSessionSampleRate: config?.replaysSessionSampleRate ?? 0.05,
    replaysOnErrorSampleRate: config?.replaysOnErrorSampleRate ?? 0.1,
  });
};

export const provideSentry: Array<Provider | EnvironmentProviders> = [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: false,
      // Preserve HTTP errors so beforeSend can identify them after Angular capture.
      extractor: (error, defaultExtractor) => {
        const originalError =
          error && typeof error === 'object' && 'ngOriginalError' in error
            ? error.ngOriginalError
            : error;
        return originalError instanceof HttpErrorResponse
          ? originalError
          : defaultExtractor(error);
      },
    }),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  provideAppInitializer(() => {
    inject(Sentry.TraceService);
  }),
];

export const sentrySetUser = (userId: string | undefined) => {
  Sentry.setUser(
    userId
      ? {
          id: userId,
          username: userId,
        }
      : null,
  );
};
